import { createSelector } from 'reselect';
import { currentCommunitySelector } from 'store/selectors/currentCommunity';
import { SubscriptionStatus } from 'types';
import { getBillingAttentionReason } from 'utils/subscriptionState';

import { IRootState } from '../reducers';

export const subscriptionsSelector = (state: IRootState) => state.subscriptions;
export const SUBSCRIPTIONS_CACHE_TTL_MS = Number(
  process.env.REACT_APP_SUBSCRIPTIONS_CACHE_TTL_MS || 10 * 60 * 1000,
);

const checkDateNotExpired = (firstDate: Date, secondDate: Date) =>
  firstDate.getTime() <= secondDate.getTime();

export const selectAllSubscriptions = createSelector(
  subscriptionsSelector,
  (subscriptions) => subscriptions.allSubscriptions || [],
);

export const selectAllOnboardingPlans = createSelector(
  subscriptionsSelector,
  (subscriptions) => subscriptions.allOnboardingPlans || [],
);

export const selectSubscriptionsFetchedAt = createSelector(
  subscriptionsSelector,
  (subscriptions) => subscriptions.allSubscriptionsFetchedAt,
);

export const selectSubscriptionsGeoKey = createSelector(
  subscriptionsSelector,
  (subscriptions) => subscriptions.allSubscriptionsGeoKey,
);

export const selectHasPlansData = createSelector(
  selectAllSubscriptions,
  (plans) => plans.length > 0,
);

export const selectIsPlansInitialLoading = createSelector(
  subscriptionsSelector,
  (subscriptions) => subscriptions.isPlansInitialLoading,
);

export const selectIsPlansRefreshing = createSelector(
  subscriptionsSelector,
  (subscriptions) => subscriptions.isPlansRefreshing,
);

export const selectArePlansStale = createSelector(
  selectSubscriptionsFetchedAt,
  (fetchedAt) => {
    if (!fetchedAt) return true;
    return Date.now() - fetchedAt > SUBSCRIPTIONS_CACHE_TTL_MS;
  },
);

export const selectCurrentUserSubscription = createSelector(
  subscriptionsSelector,
  (subscriptions) => subscriptions.currentUserSubscription,
);

export const selectBillingInvoices = createSelector(
  subscriptionsSelector,
  (subscriptions) => subscriptions.billingInvoices || [],
);

export const selectIsBillingInvoicesLoading = createSelector(
  subscriptionsSelector,
  (subscriptions) => subscriptions.isBillingInvoicesLoading,
);

export const selectBillingInvoicesLoaded = createSelector(
  subscriptionsSelector,
  (subscriptions) => Boolean(subscriptions.billingInvoicesLoaded),
);

export const selectBillingInvoicesError = createSelector(
  subscriptionsSelector,
  (subscriptions) => subscriptions.billingInvoicesError,
);

/** Saved CHF invoice billing fields from GET subscriptions (Redux). */
export const selectInvoiceBillingSource = createSelector(
  selectCurrentUserSubscription,
  (subscription) =>
    subscription
      ? {
          payerEmails: subscription.payerEmails,
          billingAddress: subscription.billingAddress,
          taxId: subscription.taxId,
        }
      : null,
);

export const selectBillingAttentionReason = createSelector(
  selectCurrentUserSubscription,
  (subscription) => getBillingAttentionReason(subscription),
);

export const selectCurrentCommunitySubscription = createSelector(
  currentCommunitySelector,
  (community) => community?.data?.subscription || null,
);

export const selectIsSubscriptionLoading = createSelector(
  subscriptionsSelector,
  (subscriptions) => subscriptions.isLoading,
);

export const selectIsSubscriptionRefreshing = createSelector(
  subscriptionsSelector,
  (subscriptions) => subscriptions.isRefreshingSubscription,
);

export const selectCurrentUserSubscriptionLoaded = createSelector(
  subscriptionsSelector,
  (subscriptions) => Boolean(subscriptions.currentUserSubscriptionLoaded),
);

export const selectIsUserSubscriptionActive = createSelector(
  selectCurrentUserSubscription,
  (subscription) => {
    const endDate = subscription?.endDate;
    if (!endDate) return true;
    return checkDateNotExpired(new Date(), new Date(endDate));
  },
);

export const selectIsCommunitySubscriptionActive = createSelector(
  selectCurrentCommunitySubscription,
  (subscription) => {
    const endDate = subscription?.endDate;
    if (!endDate) return true;
    return checkDateNotExpired(new Date(), new Date(endDate));
  },
);

export const selectCommunitySubscriptionPending = createSelector(
  selectCurrentCommunitySubscription,
  (subscription) =>
    subscription?.status === SubscriptionStatus.APPROVAL_PENDING,
);

export const selectUserSubscriptionPending = createSelector(
  selectCurrentUserSubscription,
  (subscription) =>
    subscription?.status === SubscriptionStatus.APPROVAL_PENDING,
);

export const selectPaypalPlanId = createSelector(
  subscriptionsSelector,
  (subscriptions) => subscriptions?.paypalSubscriptionPlanId,
);

export const selectIsChatsAvailable = createSelector(
  selectCurrentCommunitySubscription,
  (subscription) => subscription?.chatEnabled,
);

export const hasAdblocker = createSelector(
  subscriptionsSelector,
  (subscriptionState) => subscriptionState.hasAdblocker,
);

export const selectTrialStartErrorKey = createSelector(
  subscriptionsSelector,
  (subscriptionState) => subscriptionState.trialStartErrorKey,
);

export const selectIsStartingTrial = createSelector(
  subscriptionsSelector,
  (subscriptionState) => Boolean(subscriptionState.isStartingTrial),
);
